import { useState, type ReactNode } from 'react';
import { Button } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AccountSummary } from './account-types';
import { dayWord, daysBetween } from './account-words';
import './insights.css';
import './account.css';

export interface AccountFactsProps {
  account: AccountSummary;
  today: string;
  /** Saves the owner (an email, or null to clear); absent for a role that cannot. */
  onOwner?: ((owner: string | null) => void) | undefined;
  /** Opens the notes form on a next step; absent for a viewer. */
  onStep?: (() => void) | undefined;
}

/** "came from google / ap-automation" or "partner ACME"; a company with neither says so. */
export function cameFromWord(a: AccountSummary): string {
  const first = a.crm.acquiredPartner;
  if (first && a.plan.partner && first !== a.plan.partner)
    return `partner ${first} at sign-up, ${a.plan.partner} now`;
  if (first && !a.plan.partner) return `partner ${first} at sign-up, none now`;
  if (a.plan.partner) return `partner ${a.plan.partner}`;
  if (!a.crm.source) return 'no source recorded';
  return `came from ${[a.crm.source, a.crm.campaign].filter(Boolean).join(' / ')}`;
}

/** "customer for 17 weeks", "customer for 4 days". */
export function tenureWord(signedUpAt: string, today: string): string {
  const days = Math.max(0, daysBetween(signedUpAt.slice(0, 10), today));
  return days < 14
    ? `customer for ${days} day${days === 1 ? '' : 's'}`
    : `customer for ${Math.floor(days / 7)} weeks`;
}

function OwnerForm(x: { owner: string | null; onSave: (o: string | null) => void }): ReactNode {
  const [value, setValue] = useState(x.owner ?? '');
  return (
    <form
      className="ad-owner"
      onSubmit={(e) => {
        e.preventDefault();
        x.onSave(value.trim() || null);
      }}
    >
      <input
        aria-label="Owner's email"
        type="email"
        value={value}
        placeholder="name@ademero.com"
        onChange={(e) => setValue(e.target.value)}
      />
      <Button size="sm" type="submit">
        Save
      </Button>
    </form>
  );
}

function Owner(x: { owner: string | null; onOwner?: AccountFactsProps['onOwner'] }): ReactNode {
  const [editing, setEditing] = useState(false);
  const save = x.onOwner;
  if (editing && save)
    return <OwnerForm owner={x.owner} onSave={(o) => (save(o), setEditing(false))} />;
  return (
    <span>
      {x.owner ? `owner ${x.owner}` : <span className="ad-muted">no owner</span>}
      {save ? (
        <button type="button" className="ad-link" onClick={() => setEditing(true)}>
          {x.owner ? 'change' : 'set owner'}
        </button>
      ) : null}
    </span>
  );
}

function FactsLine({ account: a, today, onOwner }: AccountFactsProps): ReactNode {
  const f = useFormat();
  const admin = a.crm.admin;
  const parts: ReactNode[] = [
    cameFromWord(a),
    tenureWord(a.crm.signedUpAt, today),
    <Owner key="o" owner={a.crm.owner} onOwner={onOwner} />,
    admin ? `admin ${admin.name ?? admin.email}` : null,
    `last sign-in ${a.people.lastSignInAt ? f.ago(a.people.lastSignInAt) : 'never'}`,
  ].filter(Boolean);
  return (
    <p>
      {parts.map((p, i) => (
        <span key={i}>
          {i ? ' · ' : ''}
          {p}
        </span>
      ))}
    </p>
  );
}

function stepText(step: AccountSummary['crm']['nextStep'], overdue: boolean): string {
  if (!step) return 'none';
  const due = step.dueOn ? `, due ${dayWord(step.dueOn)}${overdue ? ' (overdue)' : ''}` : '';
  return `“${step.body}”${due}, ${step.author}`;
}

function StepLine({ account: a, today, onStep }: AccountFactsProps): ReactNode {
  const step = a.crm.nextStep;
  const overdue = Boolean(step?.dueOn && step.dueOn < today);
  const tone = !step ? 'ad-muted' : overdue ? 'ad-tone-danger' : undefined;
  return (
    <p className={tone}>
      <strong>Next step</strong> {overdue ? <span aria-hidden="true">⚠ </span> : null}
      {stepText(step, overdue)}
      {onStep ? (
        <button type="button" className="ad-link" onClick={onStep}>
          {step ? 'add another' : 'add one'}
        </button>
      ) : null}
    </p>
  );
}

/**
 * The title block's facts (round 2: sales M5 and S1, marketing N2, support R2-M6): where the company
 * came from and for how long, who owns it, who its admin is, then the latest note and the next step
 * with its due day, so "what did we say, and what is due" is answered above the fold.
 */
export function AccountFacts(props: AccountFactsProps): ReactNode {
  const f = useFormat();
  const note = props.account.crm.latestNote;
  return (
    <div className="ad-facts">
      <FactsLine {...props} />
      <p className={note ? undefined : 'ad-muted'}>
        <strong>Last note</strong>{' '}
        {note ? `“${note.body}” ${note.author}, ${f.ago(note.at)}` : 'none yet'}
      </p>
      <StepLine {...props} />
    </div>
  );
}
